import React, { useState, useEffect, ReactElement } from 'react';
import classNames from 'classnames/bind';
import { getFaqData } from 'api/requests';
import { useUtils } from 'utils/hooks/useUtils';
import { IQuestion, ICategory } from 'sections/Company/Faqs/types';
import { Search } from 'sections/Company/Faqs/SectionContent/Search';
import { Navigation } from 'sections/Company/Faqs/SectionContent/Navigation';
import { Questions } from 'sections/Company/Faqs/SectionContent/Questions';
import { Preloader } from 'sections/Company/Faqs/SectionContent/Preloader'
import styles from 'sections/Company/Faqs/SectionContent/SectionContent.module.scss';

const cn = classNames.bind(styles);

const SectionContent = (): ReactElement => {
  const { getDefaultActiveCategoryItems, getQuestionIdFromUrl, renderQuestions, renderFilteredQuestions } = useUtils();
  const [data, setData] = useState([] as ICategory[]);
  const [defaultActiveCategoryId, setDefaultActiveCategoryId] = useState<number | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [activeCategoryItems, setActiveCategoryItems] = useState<IQuestion[]>([]);
  const [activeQuestionId, setActiveQuestionId] = useState<number | null>(null);
  const [activeParentId, setActiveParentId] = useState<number | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchOptions, setSearchOptions] = useState<any>({
    title: false,
    contents: false,
  });
  const [filteredItems, setFilteredItems] = useState<IQuestion[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const response = await getFaqData();
      if (response.status === 200) {
        const questionIdFromUrl = getQuestionIdFromUrl();
        setData(response.data.data);
        setIsLoading(false);

        const activeCatId = questionIdFromUrl?.length
          ? questionIdFromUrl[questionIdFromUrl.length - 2]
          : response.data.data[0].id;

        setDefaultActiveCategoryId(activeCatId);
        setActiveCategoryId(activeCatId);

        !questionIdFromUrl && setActiveCategoryItems(getDefaultActiveCategoryItems(activeCatId, response.data.data));
      }
    })();
    const questionIdFromUrl = getQuestionIdFromUrl();
    if (questionIdFromUrl?.length) {
      setActiveQuestionId(questionIdFromUrl[questionIdFromUrl.length - 1]);
      setActiveCategoryId(questionIdFromUrl[questionIdFromUrl.length - 2]);
    }
  }, []);

  useEffect(() => {
    let parentId = null;
    data.forEach((category: ICategory) => {
      if (category.children) {
        category.children.forEach((child: ICategory) => {
          if (child.help_items) {
            child.help_items.forEach((question: IQuestion) => {
              if (question.id === activeQuestionId) {
                parentId = category.id;
              }
            });
          }
        });
      }
    });
    setActiveParentId(parentId);
    setActiveCategoryItems(renderQuestions(data, activeCategoryId));
  }, [data, activeQuestionId]);

  useEffect(() => {
    if (!data) return;
    const result = [...renderFilteredQuestions(data, searchOptions, searchedValue)];
    setFilteredItems(result);
    setActiveCategoryItems(result);

    if (searchedValue === '') {
      setIsSearching(false);
      setActiveCategoryItems(getDefaultActiveCategoryItems(defaultActiveCategoryId, data));
    }

    const urlId = getQuestionIdFromUrl();

    if (searchedValue === '' && urlId) {
      setIsSearching(false);
      setActiveCategoryItems(renderQuestions(data, activeCategoryId));
    }
  }, [data, isSearching, searchOptions, isSearching ? searchedValue : null]);

  useEffect(() => {
    if (searchedValue === '') {
      setIsSearching(false);
      setActiveCategoryItems(getDefaultActiveCategoryItems(defaultActiveCategoryId, data));
    }
  }, [searchedValue]);

  return (
    <section className={cn('content')}>
      <div className={cn('container')}>

        <div className={cn('search-container')}>
          <Search
            setIsSearching={setIsSearching}
            setSearchedValue={setSearchedValue}
            setSearchOptions={setSearchOptions}
          />
        </div>

        <div>
          {isLoading ? (
            <Preloader />
          ) : (
            <div className={cn('content__container')}>
              {isSearching ? (
                filteredItems.length === 0 ? (
                  <p>No results found ...</p>
                ) : (
                  <Questions
                    activeQuestionId={activeQuestionId}
                    filteredItems={filteredItems}
                    //@ts-ignore
                    // setActiveQuestionId={filteredItems.length ? 0 : setActiveQuestionId}
                    setActiveQuestionId={() => {}}
                    activeCategoryItems={activeCategoryItems}
                    questionsStyles={{ width: '100%' }}
                  />
                )
              ) : (
                <>
                  <Navigation
                    categories={data}
                    activeCategoryId={activeCategoryId}
                    setActiveCategoryId={setActiveCategoryId}
                    setActiveCategoryItems={setActiveCategoryItems}
                    activeQuestionId={activeQuestionId}
                    activeParentId={activeParentId}
                  />
                  <Questions
                    activeQuestionId={activeQuestionId}
                    setActiveQuestionId={setActiveQuestionId}
                    activeCategoryItems={activeCategoryItems}
                  />
                </>
              )}
            </div>
          )}
        </div>

      </div>
    </section>
  );
};

export default SectionContent;